import { AI_ERRORS } from './aiErrors';
import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Test-related error definitions.
 * Maps API error codes to user-friendly messages.
 *
 * AI-grader failures (`AI_*`) come from the shared `AI_ERRORS` map so the
 * wording stays in sync with the onboarding grading flow.
 */
export const TEST_ERRORS: Record<string, ErrorDefinition> = {
  ...AI_ERRORS,

  // ---------------------------------------------------------
  // Test Start Errors
  // ---------------------------------------------------------
  start_test_failed: {
    title: "Couldn't start test",
    message: 'We had trouble starting your mock interview.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  AUTHORIZATION_ERROR: {
    title: 'No attempts left',
    message: "You've used all 5 free mock interviews.",
    action: 'Upgrade to Premium for unlimited practice.',
    retryable: false,
    category: 'account',
  },

  // ---------------------------------------------------------
  // Questions Loading Errors
  // ---------------------------------------------------------
  questions_load_failed: {
    title: "Couldn't load questions",
    message: 'We had trouble loading the test questions.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  NOT_FOUND: {
    title: 'Test not found',
    message: "We couldn't find your test session.",
    action: 'Please start a new test.',
    retryable: false,
    category: 'validation',
  },

  // ---------------------------------------------------------
  // Test Completion Errors
  // ---------------------------------------------------------
  test_submit_failed: {
    title: "Couldn't submit test",
    message: 'We had trouble submitting your answers.',
    action: 'Your answers are saved. Tap to try again.',
    retryable: true,
    category: 'network',
  },

  CONFLICT: {
    title: 'Already submitted',
    message: 'This test has already been completed.',
    action: 'View your results or start a new test.',
    retryable: false,
    category: 'validation',
  },

  // ---------------------------------------------------------
  // Results Loading Errors
  // ---------------------------------------------------------
  results_load_failed: {
    title: "Couldn't load results",
    message: 'We had trouble loading your test results.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  review_load_failed: {
    title: "Couldn't load review",
    message: 'We had trouble loading the detailed review.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  // ---------------------------------------------------------
  // Network/General Errors
  // ---------------------------------------------------------
  network_offline: {
    title: "You're offline",
    message: 'The test requires an internet connection.',
    action: 'Please connect to the internet to continue.',
    retryable: true,
    category: 'network',
  },

  offline_voice: {
    title: 'Voice needs internet',
    message: 'Voice answers require an internet connection.',
    action: 'Switch to typing or connect to continue.',
    retryable: true,
    category: 'network',
    recoverySuggestions: [
      'Connect to Wi-Fi or mobile data',
      'Switch to typing mode to continue',
    ],
  },

  VALIDATION_ERROR: {
    title: 'Invalid request',
    message: 'Something was wrong with the request.',
    action: 'Please try again.',
    retryable: true,
    category: 'validation',
  },

  AUTHENTICATION_ERROR: {
    title: 'Session expired',
    message: 'Your session has expired.',
    action: 'Please sign in again to continue.',
    retryable: false,
    category: 'authentication',
  },

  RATE_LIMIT_EXCEEDED: {
    title: 'Too many requests',
    message: "You're making requests too quickly.",
    action: 'Please wait a moment and try again.',
    retryable: true,
    retryAfterSeconds: 10,
    category: 'rate_limit',
  },

  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    action: 'Please try again.',
    retryable: true,
    category: 'server',
  },
};

/**
 * Get a friendly error for a test-related error code
 */
export function getTestError(code: string): FriendlyError {
  const errorDef = TEST_ERRORS[code] ?? {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return {
    ...errorDef,
    code,
  };
}
